import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { message } from 'antd';
import {
    useCreateTestConnectionRequestMutation,
    useGetIngestionExecutionRequestLazyQuery,
} from '../../../../../../graphql/ingestion.generated';
import { FAILURE, RUNNING } from '../../../utils';
import { TestConnectionResult } from './types';
import { useTranslation } from '../../../../../../i18n/useTranslation';

export interface TestConnectionPollingContextType {
    // State
    isLoading: boolean;
    pollingInterval: NodeJS.Timeout | null;
    testConnectionResult: TestConnectionResult | null;
    testConnectionFailureCheck: boolean;
    showTestConnectionResultCollapsible: boolean;
    isModalVisible: boolean;

    // Setters
    setIsLoading: (loading: boolean) => void;
    setPollingInterval: (interval: NodeJS.Timeout | null) => void;
    setTestConnectionResult: (result: TestConnectionResult | null) => void;
    setTestConnectionFailureCheck: (check: boolean) => void;
    setShowTestConnectionResultCollapsible: (show: boolean) => void;
    setIsReset: (reset: boolean) => void;
    setIsModalVisible: (visible: boolean) => void;

    // API functions
    createTestConnectionRequest: ReturnType<typeof useCreateTestConnectionRequestMutation>[0];
    getIngestionExecutionRequest: ReturnType<typeof useGetIngestionExecutionRequestLazyQuery>[0];

    // Data
    requestData: ReturnType<typeof useCreateTestConnectionRequestMutation>[1]['data'];
    resultData: ReturnType<typeof useGetIngestionExecutionRequestLazyQuery>[1]['data'];
    loading: ReturnType<typeof useGetIngestionExecutionRequestLazyQuery>[1]['loading'];

    // Utility functions
    startPolling: (requestUrn: string) => void;
    stopPolling: () => void;
    cleanupPolling: () => void;
    resetTestConnection: () => void;
}

const DefaultTestConnectionPollingContext: TestConnectionPollingContextType = {
    isLoading: false,
    pollingInterval: null,
    testConnectionResult: null,
    testConnectionFailureCheck: false,
    showTestConnectionResultCollapsible: false,
    isModalVisible: false,
    setIsLoading: () => {},
    setPollingInterval: () => {},
    setTestConnectionResult: () => {},
    setTestConnectionFailureCheck: () => {},
    setShowTestConnectionResultCollapsible: () => {},
    setIsReset: () => {},
    createTestConnectionRequest: () => Promise.resolve({ data: null }),
    getIngestionExecutionRequest: () => Promise.resolve({ data: null }),
    requestData: undefined,
    resultData: undefined,
    loading: false,
    startPolling: () => {},
    stopPolling: () => {},
    cleanupPolling: () => {},
    resetTestConnection: () => {},
    setIsModalVisible: () => {},
};

const TestConnectionPollingContext = createContext<TestConnectionPollingContextType>(
    DefaultTestConnectionPollingContext,
);

interface TestConnectionPollingProviderProps {
    children: ReactNode;
}

export const TestConnectionPollingProvider = ({ children }: TestConnectionPollingProviderProps) => {
    const { t } = useTranslation();

    // State
    const [isLoading, setIsLoading] = useState(false);
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
    const [testConnectionResult, setTestConnectionResult] = useState<TestConnectionResult | null>(null);
    const [testConnectionFailureCheck, setTestConnectionFailureCheck] = useState(false);
    const [showTestConnectionResultCollapsible, setShowTestConnectionResultCollapsible] = useState(false);
    const [isReset, setIsReset] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // API hooks
    const [createTestConnectionRequest, { data: requestData }] = useCreateTestConnectionRequestMutation();
    const [getIngestionExecutionRequest, { data: resultData, loading }] = useGetIngestionExecutionRequestLazyQuery();

    // Utility functions
    const startPolling = (requestUrn: string) => {
        const interval = setInterval(
            () =>
                getIngestionExecutionRequest({
                    variables: { urn: requestUrn },
                }),
            2000,
        );
        setPollingInterval(interval);
    };

    const stopPolling = () => {
        if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
        }
    };

    const cleanupPolling = () => {
        stopPolling();
        setIsLoading(false);
    };

    // Effect to start polling when request is created
    useEffect(() => {
        if (requestData && requestData.createTestConnectionRequest) {
            setIsReset(false); // Reset the flag when starting a new test connection
            startPolling(requestData.createTestConnectionRequest as string);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestData, getIngestionExecutionRequest]);

    // Effect to handle polling results
    useEffect(() => {
        // Ignore resultData if we've reset the connection
        if (!loading && resultData && !isReset) {
            const result = resultData.executionRequest?.result;
            if (result && result.status !== RUNNING) {
                if (result.status === FAILURE) {
                    if (result.report) {
                        message.error(result.report);
                    } else if (result.structuredReport) {
                        message.error(JSON.parse(result.structuredReport.serializedValue).mitigation_message);
                    } else {
                        message.error(
                            t(
                                'Something went wrong with your connection test. Please check your recipe and try again.',
                            ),
                        );
                    }
                    setTestConnectionResult(null);
                    setShowTestConnectionResultCollapsible(false);
                    setTestConnectionFailureCheck(true);
                    setIsModalVisible(false);
                } else if (result.structuredReport) {
                    // Only process structured report for non-failure cases
                    const testConnectionReport = JSON.parse(result.structuredReport.serializedValue);
                    setTestConnectionFailureCheck(
                        testConnectionReport?.capability_report?.USAGE_STATS?.capable === false ||
                            testConnectionReport?.capability_report?.DATA_PROFILING?.capable === false ||
                            testConnectionReport?.basic_connectivity?.capable === false,
                    );
                    setTestConnectionResult(testConnectionReport);
                    setShowTestConnectionResultCollapsible(true);
                }
                stopPolling();
                setIsLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultData, loading, t, isReset]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            cleanupPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetTestConnection = () => {
        setTestConnectionResult(null);
        setShowTestConnectionResultCollapsible(false);
        setTestConnectionFailureCheck(false);
        setIsReset(true); // Mark as reset to ignore future resultData processing
        cleanupPolling();
    };

    const contextValue: TestConnectionPollingContextType = {
        // State
        isLoading,
        pollingInterval,
        testConnectionResult,
        testConnectionFailureCheck,
        showTestConnectionResultCollapsible,
        isModalVisible,

        // Setters
        setIsLoading,
        setPollingInterval,
        setTestConnectionResult,
        setTestConnectionFailureCheck,
        setShowTestConnectionResultCollapsible,
        setIsReset,
        setIsModalVisible,

        // API functions
        createTestConnectionRequest,
        getIngestionExecutionRequest,

        // Data
        requestData,
        resultData,
        loading,

        // Utility functions
        startPolling,
        stopPolling,
        cleanupPolling,
        resetTestConnection,
    };

    return (
        <TestConnectionPollingContext.Provider value={contextValue}>{children}</TestConnectionPollingContext.Provider>
    );
};

export const useTestConnectionPolling = (): TestConnectionPollingContextType => {
    return useContext(TestConnectionPollingContext);
};
